/**
 * EvidenceRequestsSection — assessor evidence requests on a register item.
 *
 * PR-Cluster-6-c (docs/ux-design-plan.md §10). Mirrors the Q&A section
 * shape: renders every evidence request an assessor opened against
 * this control, so a workspace member (Marcus / Nadia) can attach the
 * evidence + close the request out.
 *
 * Requests are opened ONLY from assessor sessions (server-side gate
 * in evidence-requests.ts). Workspace members here provide + owners
 * close.
 *
 * Deferred:
 *   - Evidence-artifact picker component (currently the workspace
 *     member pastes the artifact_id; a real picker landing when the
 *     evidence-library-picker DS component is built)
 *   - Reroute-request affordance (BH24 counterpart)
 *   - POA&M escalation UI is out of scope for the workspace-member
 *     surface (assessor-only endpoint)
 */

import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Field, Input, Spinner, Textarea } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../../lib/workspace';
import {
  closeEvidenceRequest,
  listEvidenceRequestsForItem,
  provideEvidenceRequest,
  type EvidenceRequestRow,
  type EvidenceRequestSeverity,
  type EvidenceRequestStatus,
} from '../../lib/api';
import { fmtDate } from '../../lib/format';

export function EvidenceRequestsSection({ registerItemId }: { registerItemId: string }) {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const canClose =
    workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin';

  const [rows, setRows] = useState<EvidenceRequestRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await listEvidenceRequestsForItem(slug, registerItemId);
        if (!cancelled) setRows(res.requests);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load evidence requests.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, registerItemId, reload]);

  const refresh = useCallback(() => setReload((r) => r + 1), []);
  if (!slug) return null;

  const openRows = rows?.filter((r) => r.status !== 'closed') ?? [];
  const closedRows = rows?.filter((r) => r.status === 'closed') ?? [];

  return (
    <section
      aria-labelledby="evidence-requests-heading"
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        padding: 'var(--space-5)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <header style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h2 id="evidence-requests-heading" style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
            Evidence requests
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
            Formal requests your 3PAO opened against this control. Attach the artifact from your
            evidence library, then close the request.
          </p>
        </div>
        {rows && rows.length > 0 ? (
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            {openRows.length} open · {closedRows.length} closed
          </span>
        ) : null}
      </header>

      {loading ? (
        <Spinner label="Loading requests" />
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : rows == null || rows.length === 0 ? (
        <EmptyRequests />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {openRows.map((r) => (
            <RequestCard
              key={r.request_id}
              slug={slug}
              request={r}
              canClose={canClose}
              onChanged={refresh}
            />
          ))}
          {closedRows.length > 0 ? (
            <details style={{ marginTop: 'var(--space-2)' }}>
              <summary
                style={{
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-3xs)',
                  padding: 'var(--space-1) 0',
                }}
              >
                Show {closedRows.length} closed
              </summary>
              <div style={{ display: 'grid', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
                {closedRows.map((r) => (
                  <RequestCard
                    key={r.request_id}
                    slug={slug}
                    request={r}
                    canClose={canClose}
                    onChanged={refresh}
                  />
                ))}
              </div>
            </details>
          ) : null}
        </div>
      )}
    </section>
  );
}

function EmptyRequests() {
  return (
    <div
      style={{
        padding: 'var(--space-4)',
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-2xs)',
        background: 'var(--surface-alt)',
        borderRadius: 'var(--radius-card)',
        textAlign: 'center',
      }}
    >
      No evidence requests on this item yet.
    </div>
  );
}

// ---- Request card ----------------------------------------------------

function RequestCard(props: {
  slug: string;
  request: EvidenceRequestRow;
  canClose: boolean;
  onChanged: () => void;
}) {
  const { request: r, canClose, onChanged, slug } = props;
  const [showProvide, setShowProvide] = useState(false);
  const [showClose, setShowClose] = useState(false);
  return (
    <article
      style={{
        border: '1px solid var(--border)',
        borderLeftWidth: '3px',
        borderLeftColor: statusBorder(r.status, r.severity),
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface-elevated)',
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <header style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <SeverityPill severity={r.severity} />
        <StatusPill status={r.status} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }} title={r.opened_by}>
          {r.opened_by}
        </span>
        <time dateTime={r.opened_at} style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          {fmtDate(r.opened_at)}
        </time>
      </header>

      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--text)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {r.reason_text}
      </p>

      {r.provided_at ? (
        <div
          style={{
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--success-bg)',
            border: '1px solid var(--band-green)',
            borderLeftWidth: '3px',
            borderRadius: '4px',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--success-fg)',
          }}
        >
          ✓ Provided by <span style={{ fontFamily: 'var(--font-mono)' }} title={r.provided_by ?? ''}>{r.provided_by}</span>
          {' '}on <time dateTime={r.provided_at}>{fmtDate(r.provided_at)}</time>
          {r.provided_evidence_artifact_id ? (
            <> · 📎 <code style={{ fontFamily: 'var(--font-mono)' }} title={r.provided_evidence_artifact_id}>
              {r.provided_evidence_artifact_id.slice(0, 16)}…
            </code></>
          ) : null}
          {r.provided_note ? (
            <div style={{ marginTop: 4, color: 'var(--text)', fontStyle: 'italic' }}>“{r.provided_note}”</div>
          ) : null}
        </div>
      ) : null}

      {r.status === 'closed' ? (
        <div
          style={{
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--surface-alt)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-muted)',
          }}
        >
          Closed <time dateTime={r.closed_at ?? r.opened_at}>{fmtDate(r.closed_at ?? r.opened_at)}</time>
          {r.closed_reason ? (
            <>
              {' — '}
              <span style={{ fontStyle: 'italic' }}>“{r.closed_reason}”</span>
            </>
          ) : null}
        </div>
      ) : null}

      {r.status !== 'closed' ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', paddingTop: 'var(--space-1)', borderTop: '1px solid var(--border)' }}>
          {r.status === 'open' ? (
            <Button variant="ghost" compact onClick={() => setShowProvide((s) => !s)}>
              {showProvide ? 'Cancel provide' : 'Provide evidence'}
            </Button>
          ) : null}
          {canClose ? (
            <Button variant="ghost" compact onClick={() => setShowClose((s) => !s)}>
              {showClose ? 'Cancel close' : 'Close request'}
            </Button>
          ) : null}
        </div>
      ) : null}

      {showProvide ? (
        <ProvideForm
          slug={slug}
          requestId={r.request_id}
          onDone={() => {
            setShowProvide(false);
            onChanged();
          }}
          onCancel={() => setShowProvide(false)}
        />
      ) : null}
      {showClose ? (
        <CloseForm
          slug={slug}
          requestId={r.request_id}
          onDone={() => {
            setShowClose(false);
            onChanged();
          }}
          onCancel={() => setShowClose(false)}
        />
      ) : null}
    </article>
  );
}

function SeverityPill({ severity }: { severity: EvidenceRequestSeverity }) {
  const tone: Record<EvidenceRequestSeverity, { bg: string; fg: string; label: string }> = {
    clarification: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)', label: 'Clarification' },
    critical_finding: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)', label: 'Critical finding' },
  };
  const t = tone[severity];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function StatusPill({ status }: { status: EvidenceRequestStatus }) {
  const tone: Record<EvidenceRequestStatus, { bg: string; fg: string; label: string }> = {
    open: { bg: 'var(--surface-alt)', fg: 'var(--text-muted)', label: 'Open' },
    evidence_provided: { bg: 'var(--accent)', fg: 'var(--text-inverse)', label: 'Provided' },
    closed: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', label: 'Closed' },
  };
  const t = tone[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function statusBorder(status: EvidenceRequestStatus, severity: EvidenceRequestSeverity): string {
  if (status === 'closed') return 'var(--border)';
  if (severity === 'critical_finding') return 'var(--band-red)';
  return 'var(--band-amber)';
}

// ---- Provide + Close forms ------------------------------------------

function ProvideForm(props: {
  slug: string;
  requestId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [artifactId, setArtifactId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await provideEvidenceRequest(props.slug, props.requestId, {
        evidence_artifact_id: artifactId.trim(),
        note: note.trim() || undefined,
      });
      props.onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Provide failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <strong style={{ color: 'var(--text)', fontWeight: 700 }}>Provide evidence</strong>
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
        Attach an evidence artifact from your library by pasting its id. A real picker lands in
        a follow-on; for now, copy the id from the Evidence page.
      </p>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id={`provide-artifact-${props.requestId}`} label="Evidence artifact id">
        <Input
          id={`provide-artifact-${props.requestId}`}
          required
          value={artifactId}
          onChange={(e) => setArtifactId(e.target.value)}
          placeholder="art_..."
          autoComplete="off"
        />
      </Field>
      <Field id={`provide-note-${props.requestId}`} label="Note (optional)">
        <Textarea
          id={`provide-note-${props.requestId}`}
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Context for the assessor about why this artifact answers the request."
        />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy || artifactId.trim().length === 0}>
          {busy ? 'Providing…' : 'Provide'}
        </Button>
      </div>
    </form>
  );
}

function CloseForm(props: {
  slug: string;
  requestId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await closeEvidenceRequest(props.slug, props.requestId, reason.trim());
      props.onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Close failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--surface-alt)',
        border: '1px solid var(--border)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <strong style={{ color: 'var(--text)', fontWeight: 700 }}>Close request</strong>
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
        Reason ≥ 20 chars, audit-logged. Assessor is notified.
      </p>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id={`close-${props.requestId}`} label="Reason">
        <Textarea
          id={`close-${props.requestId}`}
          required
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy || reason.trim().length < 20}>
          {busy ? 'Closing…' : 'Confirm close'}
        </Button>
      </div>
    </form>
  );
}
